import { useState } from 'react';
import { CheckCheck, ChevronDown, ClipboardCheck, Plus, Video } from 'lucide-react';
import { BaseLayout } from '@/shared/components/layout';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { useToast } from '@/shared/hooks/use-toast';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { cn } from '@/shared/utils/utils';
import { useProjectAreaTagLocationOptions } from '@/features/area-tag-sets';
import {
  useCostCodes,
  useProjectUsers,
  usePunchListItemTypes,
  usePunchWalkthroughs,
  useTradePartners,
  useTrades,
} from '../hooks';
import { PunchListsNavigation } from './PunchListsNavigation';
import { PunchWalkthroughDialog } from './PunchWalkthroughDialog';
import { WalkthroughDraftsPanel } from './WalkthroughDraftsPanel';

export function PunchWalkthroughsWorkspace({ projectId }: { projectId: number }) {
  const [page, setPage] = useState(1);
  const [history, setHistory] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [capture, setCapture] = useState<{ sessionId?: string } | null>(null);
  const walkthroughs = usePunchWalkthroughs(projectId, page, history ? 'history' : 'active');
  const { data: trades = [] } = useTrades();
  const { data: itemTypes = [] } = usePunchListItemTypes();
  const { data: locations } = useProjectAreaTagLocationOptions(projectId);
  const { data: users = [] } = useProjectUsers(projectId);
  const { data: tradePartners = [] } = useTradePartners(projectId);
  const { data: costCodes = [] } = useCostCodes();
  const { toast } = useToast();

  return (
    <div className="min-w-0 space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Punch Lists
          </p>
          <h1 className="font-display text-2xl font-extrabold tracking-[-0.02em] sm:text-3xl">
            AI Walkthroughs
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Turn a site walkthrough into punch items you review and approve.
          </p>
        </div>
        <Button className="w-full gap-2 sm:w-auto" onClick={() => setCapture({})}>
          <Plus className="size-4" /> New walkthrough
        </Button>
      </div>

      <PunchListsNavigation projectId={projectId} />

      <Card className="p-3 shadow-slate sm:p-5 lg:p-6">
        <Button
          variant="ghost"
          className="w-full justify-between gap-2 sm:hidden"
          aria-expanded={showGuide}
          aria-controls="walkthrough-guide"
          onClick={() => setShowGuide(!showGuide)}
        >
          <span className="flex items-center gap-2">
            <Video className="size-4 text-primary" /> How walkthroughs work
          </span>
          <ChevronDown className={cn('size-4 transition-transform', showGuide && 'rotate-180')} />
        </Button>
        <div
          id="walkthrough-guide"
          className={cn(
            'gap-6 sm:grid lg:grid-cols-[minmax(0,0.8fr)_minmax(0,2fr)] lg:gap-8',
            showGuide ? 'grid px-2 pb-2 pt-4 sm:p-0' : 'hidden'
          )}
        >
          <div>
            <div className="mb-3 flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Video className="size-5" />
            </div>
            <h2 className="font-display text-lg font-bold tracking-tight">
              Walk it. Review it. Resolve it.
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Record on site or upload a video up to five minutes long. Describe each issue as you
              go.
            </p>
          </div>
          <ol className="grid gap-5 sm:grid-cols-3 lg:items-center">
            {[
              { icon: Video, title: 'Capture', description: 'Show the issue and say where it is.' },
              {
                icon: ClipboardCheck,
                title: 'Review',
                description: 'Check the suggested details and photos. Edit or discard each draft.',
              },
              {
                icon: CheckCheck,
                title: 'Publish',
                description: 'Approved items join your punch list, ready for follow-up.',
              },
            ].map(({ icon: Icon, title, description }, index) => (
              <li key={title} className="flex gap-3 sm:block">
                <div className="mb-3 flex items-center gap-2 text-primary">
                  <Icon className="size-4" />
                  <span className="text-xs font-bold">0{index + 1}</span>
                </div>
                <div>
                  <h3 className="text-sm font-semibold">{title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {description}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Card>

      <WalkthroughDraftsPanel
        sessions={walkthroughs.data?.items}
        isLoading={walkthroughs.isLoading}
        error={walkthroughs.error}
        onRetry={() => void walkthroughs.refetch()}
        count={walkthroughs.data?.count}
        page={page}
        hasNext={!!walkthroughs.data?.next}
        onPageChange={setPage}
        history={history}
        onHistoryChange={(next) => {
          setHistory(next);
          setPage(1);
        }}
        onOpen={(sessionId) => setCapture({ sessionId })}
      />

      {capture && (
        <PunchWalkthroughDialog
          key={capture.sessionId ?? 'new'}
          open
          onOpenChange={(open) => {
            if (!open) setCapture(null);
          }}
          projectId={projectId}
          initialSessionId={capture.sessionId}
          trades={trades}
          itemTypes={itemTypes}
          locations={locations?.areaTags ?? []}
          users={users}
          tradePartners={tradePartners}
          costCodes={costCodes}
          onPublished={(count) =>
            toast({
              title: 'Punch items created',
              description: `${count} item${count === 1 ? '' : 's'} added from the walkthrough.`,
            })
          }
        />
      )}
    </div>
  );
}

export function PunchWalkthroughsPage() {
  const { projectIdNumber } = useCurrentProject();
  return (
    <BaseLayout>
      {projectIdNumber ? (
        <PunchWalkthroughsWorkspace key={projectIdNumber} projectId={projectIdNumber} />
      ) : (
        <div className="p-6 text-sm text-muted-foreground">
          Select a project to view AI walkthroughs.
        </div>
      )}
    </BaseLayout>
  );
}
